import { buttonVariants, cn } from '@napaicp/ui'
import { ArrowUpRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { partnerBrandAssets, type PartnerBrandKey } from '@/lib/brand-assets'
import { institutionalLinks } from '@/lib/institutional-links'
import { programTeam } from '@/lib/program-team'

const PARTNER_UNIVERSITIES = ['ncu', 'ntpu', 'nthu'] as const

interface OrgLogoLinkProps {
  orgKey: PartnerBrandKey
  /** Tailwind height classes: mobile first, then desktop (`sm:`). */
  heightClassName: string
}

/**
 * One institution logo, stacked above its full name (BR-110: the image
 * alone must never carry all the information) with a visible `↗` glyph
 * marking it as an outbound link — never a printed raw URL.
 */
function OrgLogoLink({ orgKey, heightClassName }: OrgLogoLinkProps) {
  const { t } = useTranslation('about')
  const name = t(`team.orgs.${orgKey}.name`)

  return (
    <a
      href={institutionalLinks[orgKey]}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('team.visitSite', { name })}
      className="flex flex-col items-center gap-2 rounded-md text-center transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
    >
      <img
        src={partnerBrandAssets[orgKey].svg}
        alt=""
        className={`${heightClassName} w-auto max-w-full object-contain`}
      />
      <span className="flex items-center gap-1 text-sm text-muted-foreground">
        {name}
        <ArrowUpRight className="size-4 shrink-0" aria-hidden="true" />
      </span>
    </a>
  )
}

/**
 * Institutional trust / credibility section (PR-117) — shared verbatim by
 * the homepage (`/`, after every product/feature section and before the
 * global `SiteFooter`) and `/about` `#team`, so the two pages never drift
 * apart (the homepage's copy is the source of truth; `/about` renders this
 * same component rather than a second, separately-maintained version).
 *
 * Every logo and every PI/co-PI photo is a real outbound link to that
 * party's own official page (BR-109/BR-110): `alt=""` plus visible name
 * text (A11Y-BRAND-002), an `aria-label` stating the link's destination
 * (A11Y-BRAND-003), and a visible `↗` glyph instead of a printed raw URL.
 */
export function InstitutionalTrustSection() {
  const { t } = useTranslation(['home', 'about'])

  return (
    <section
      aria-labelledby="institutional-trust-heading"
      className="flex flex-col gap-8 rounded-2xl border border-border bg-muted/30 px-4 py-8 sm:px-8 sm:py-10"
    >
      <h2
        id="institutional-trust-heading"
        className="text-center text-2xl font-semibold tracking-tight sm:text-3xl"
      >
        {t('home:trust.heading')}
      </h2>

      <p className="mx-auto max-w-2xl text-center text-muted-foreground">{t('home:trust.intro')}</p>

      <div className="flex flex-wrap items-center justify-center gap-6">
        <OrgLogoLink orgKey="napai" heightClassName="h-16 sm:h-20" />
        <span aria-hidden="true" className="text-2xl font-light text-muted-foreground">
          ×
        </span>
        <OrgLogoLink orgKey="ai4x" heightClassName="h-[72px] sm:h-24" />
      </div>

      <div className="flex flex-col items-center gap-3">
        <p className="text-sm font-medium text-muted-foreground">
          {t('home:trust.sponsorHeading')}
        </p>
        <OrgLogoLink orgKey="moe-ite" heightClassName="h-10 sm:h-12" />
      </div>

      <div className="flex flex-col items-center gap-3">
        <p className="text-sm font-medium text-muted-foreground">
          {t('home:trust.partnersHeading')}
        </p>
        <div className="flex flex-wrap items-start justify-center gap-8">
          {PARTNER_UNIVERSITIES.map((key) => (
            <OrgLogoLink key={key} orgKey={key} heightClassName="h-10 sm:h-12" />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <p className="text-center text-sm font-medium text-muted-foreground">
          {t('home:trust.teamHeading')}
        </p>
        <div className="grid gap-4 sm:grid-cols-3">
          {programTeam.map(({ key, roleKey, url, photo }) => {
            const name = t(`home:trust.members.${key}.name`)
            return (
              <a
                key={key}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('home:trust.visitPerson', { name })}
                className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-4 text-center transition-colors hover:bg-muted/50"
              >
                <img src={photo} alt="" className="size-20 rounded-full object-cover" />
                <span className="font-medium">
                  {name}｜{t(`home:trust.roles.${roleKey}`)}
                </span>
                <span className="text-sm text-muted-foreground">
                  {t(`home:trust.members.${key}.affiliation`)}
                </span>
                <ArrowUpRight
                  className="size-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
              </a>
            )
          })}
        </div>
      </div>

      <div className="flex flex-wrap justify-center gap-3">
        <a
          href={institutionalLinks.napai}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(buttonVariants({ variant: 'outline' }), 'gap-1.5')}
        >
          {t('home:trust.visitProgram')}
          <ArrowUpRight className="size-4" aria-hidden="true" />
        </a>
        <a
          href={institutionalLinks.ai4x}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(buttonVariants({ variant: 'outline' }), 'gap-1.5')}
        >
          {t('home:trust.visitCenter')}
          <ArrowUpRight className="size-4" aria-hidden="true" />
        </a>
      </div>
    </section>
  )
}
